import { useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { Base, CheckIcon, ChevronDownIcon } from '@itsrever/design-system'
import {
  PbECommerceSettingsPlatformEnum,
  SearchCatalogKindEnum
} from '@itsrever/rever-api'
import { useCatalogItems, useCatalogSearch } from '@/application/queries'
import { CatalogItem, CatalogKind } from '@/domain/catalog'
import { FullCatalog } from '@/domain/exchanges'
import {
  CatalogCopyKey,
  CatalogPicker,
  FreeTextCopyKey,
  FreeTextListField
} from '@/ui/components/settings-components/non-returnable-form'
import { FormProps } from './exchanges-forms'
import { SettingsCard, SwitchRow } from '@/ui/components/group-settings'

const {
  Button,
  Callout,
  Card,
  FieldDescription,
  FieldLabel,
  FieldRoot,
  Input,
  ToggleGroup,
  ToggleGroupItem,
  ComboboxRoot,
  ComboboxInputGroup,
  ComboboxInput,
  ComboboxList,
  ComboboxItemDetail,
  ComboboxEmpty,
  ComboboxLoading,
  ComboboxCheckItem,
  ComboboxChevronTrigger,
  ComboboxDropdown
} = Base

type ExclusionKey =
  | 'excludedProductIds'
  | 'excludedCollectionIds'
  | 'excludedTags'

const EXCLUSIONS: {
  field: ExclusionKey
  kind: CatalogKind
  copyKey: CatalogCopyKey
}[] = [
  {
    field: 'excludedProductIds',
    kind: SearchCatalogKindEnum.Product,
    copyKey: 'products'
  },
  {
    field: 'excludedCollectionIds',
    kind: SearchCatalogKindEnum.Collection,
    copyKey: 'collections'
  },
  {
    field: 'excludedTags',
    kind: SearchCatalogKindEnum.ProductTag,
    copyKey: 'product_tags'
  }
]

// Other platforms can't search their catalog, so the lists ecommerce-api matches there are typed in, as in
// Non-returnable items. Their categories go where Shopify keeps its collections.
const TYPED_EXCLUSIONS: Record<
  string,
  { field: ExclusionKey; copyKey: FreeTextCopyKey }[]
> = {
  [PbECommerceSettingsPlatformEnum.Prestashop]: [
    { field: 'excludedProductIds', copyKey: 'product_ids' },
    { field: 'excludedCollectionIds', copyKey: 'category_ids' },
    { field: 'excludedTags', copyKey: 'tag_ids' }
  ],
  [PbECommerceSettingsPlatformEnum.Woocommerce]: [
    { field: 'excludedProductIds', copyKey: 'product_ids' },
    { field: 'excludedCollectionIds', copyKey: 'category_ids' },
    { field: 'excludedTags', copyKey: 'tag_ids' }
  ],
  [PbECommerceSettingsPlatformEnum.Salesforce]: [
    { field: 'excludedProductIds', copyKey: 'product_ids' },
    { field: 'excludedCollectionIds', copyKey: 'category_ids' }
  ]
}

// The languages a portal's copies come in.
const LANGUAGES = ['EN', 'ES', 'FR', 'DE', 'IT', 'PT', 'NL', 'PL', 'CS', 'SK']

export const FullCatalogForm: React.FC<
  FormProps<FullCatalog> & {
    // A portal on the store, to search its catalog.
    ecommerceId?: string
    platform?: string
  }
> = ({ value, saved, onChange, readOnly, ecommerceId, platform }) => {
  const { t } = useTranslation()
  const isShopify = platform === PbECommerceSettingsPlatformEnum.Shopify
  const set = (field: ExclusionKey) => (ids: string[]) =>
    onChange({ ...value, [field]: ids })
  return (
    <>
      {isShopify ? (
        <SettingsCard
          title={t('exchanges.catalog.menu_title')}
          description={<MenuDescription />}
        >
          <MenuPicker
            ecommerceId={ecommerceId}
            value={value.menuId}
            onChange={(menuId) => onChange({ ...value, menuId })}
            readOnly={readOnly}
          />
        </SettingsCard>
      ) : null}
      {isShopify
        ? EXCLUSIONS.map((exclusion) => (
            <ListCard key={exclusion.field}>
              <ExclusionPicker
                kind={exclusion.kind}
                copyKey={exclusion.copyKey}
                ecommerceId={ecommerceId}
                value={value[exclusion.field]}
                saved={saved[exclusion.field]}
                onChange={set(exclusion.field)}
                readOnly={readOnly}
              />
            </ListCard>
          ))
        : (TYPED_EXCLUSIONS[platform ?? ''] ?? []).map((exclusion) => (
            <ListCard key={exclusion.field}>
              <FreeTextListField
                bare
                copyPrefix="exchanges.catalog"
                copyKey={exclusion.copyKey}
                platform={platform}
                value={value[exclusion.field]}
                onChange={set(exclusion.field)}
                canEdit={!readOnly}
              />
            </ListCard>
          ))}
      {isShopify ? (
        <>
          <SettingsCard title={t('exchanges.catalog.channels_title')}>
            <SwitchRow
              label={t('exchanges.catalog.online_store_label')}
              description={t('exchanges.catalog.online_store_description')}
              checked={value.onlineStoreOnly}
              onChange={(onlineStoreOnly) =>
                onChange({ ...value, onlineStoreOnly })
              }
              readOnly={readOnly}
            />
          </SettingsCard>
          <ExtraDetailsCard
            titles={value.extraTitles ?? {}}
            onChange={(extraTitles) => onChange({ ...value, extraTitles })}
            readOnly={readOnly}
          />
        </>
      ) : null}
    </>
  )
}

const ListCard = ({ children }: { children: React.ReactNode }) => (
  <Card variant="flat" className="flex flex-col gap-3 p-5">
    {children}
  </Card>
)

// Each product shows its metafield under this title on the Full Catalog product page.
function ExtraDetailsCard({
  titles,
  onChange,
  readOnly
}: {
  titles: Record<string, string>
  onChange: (titles: Record<string, string>) => void
  readOnly: boolean
}) {
  const { t } = useTranslation()
  const [language, setLanguage] = useState(LANGUAGES[0])
  const [showSteps, setShowSteps] = useState(false)
  const title = titles[language] ?? ''
  const code = <code className="font-mono" />
  return (
    <SettingsCard
      title={t('exchanges.catalog.extra_details.title')}
      description={t('exchanges.catalog.extra_details.description')}
    >
      <Callout tone="neutral" className="text-xs leading-relaxed">
        <div className="flex flex-col gap-2">
          <div className="flex items-center justify-between gap-2">
            <p className="text-xs">
              <Trans
                i18nKey="exchanges.catalog.extra_details.metafield"
                components={{ code }}
              />
            </p>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setShowSteps((shown) => !shown)}
            >
              {t(
                showSteps
                  ? 'exchanges.catalog.extra_details.hide_steps'
                  : 'exchanges.catalog.extra_details.show_steps'
              )}
            </Button>
          </div>
          {showSteps ? (
            <ol className="list-decimal pl-5 text-xs">
              {[1, 2, 3].map((step) => (
                <li key={step}>
                  <Trans
                    i18nKey={`exchanges.catalog.extra_details.step_${step}`}
                    components={{ code }}
                  />
                </li>
              ))}
            </ol>
          ) : null}
        </div>
      </Callout>
      <FieldRoot>
        <FieldLabel>{t('exchanges.catalog.extra_details.language')}</FieldLabel>
        <ToggleGroup
          variant="button"
          aria-label={t('exchanges.catalog.extra_details.language')}
          value={[language]}
          onValueChange={(next) => next.length > 0 && setLanguage(next[0])}
        >
          {LANGUAGES.map((lang) => (
            <ToggleGroupItem key={lang} value={lang}>
              {lang}
            </ToggleGroupItem>
          ))}
        </ToggleGroup>
      </FieldRoot>
      <FieldRoot className="max-w-md">
        <FieldLabel>
          {t('exchanges.catalog.extra_details.section_title')}
        </FieldLabel>
        <Input
          value={title}
          disabled={readOnly}
          onChange={(event) =>
            onChange({ ...titles, [language]: event.currentTarget.value })
          }
        />
      </FieldRoot>
      <Card variant="muted" className="flex flex-col gap-1 p-3">
        <p className="text-xs text-neutral-600">
          {t('exchanges.catalog.extra_details.preview')}
        </p>
        <p className="flex items-center gap-1 text-sm text-neutral-900">
          <ChevronDownIcon className="size-4" />
          {title}
        </p>
        <p className="pl-5 text-xs text-neutral-600">
          {t('exchanges.catalog.extra_details.preview_example')}
        </p>
      </Card>
    </SettingsCard>
  )
}

// What the saved settings hide, beside the form.
export function CatalogSummary({
  value,
  platform
}: {
  value: FullCatalog
  platform?: string
}) {
  const { t } = useTranslation()
  const isShopify = platform === PbECommerceSettingsPlatformEnum.Shopify
  const hasTags = isShopify || TYPED_EXCLUSIONS[platform ?? '']?.length === 3
  const rows: [string, string | number][] = [
    [t('exchanges.catalog.summary.products'), value.excludedProductIds.length],
    [
      t(
        isShopify
          ? 'exchanges.catalog.summary.collections'
          : 'exchanges.catalog.summary.categories'
      ),
      value.excludedCollectionIds.length
    ],
    ...(hasTags
      ? [
          [t('exchanges.catalog.summary.tags'), value.excludedTags.length] as [
            string,
            number
          ]
        ]
      : []),
    ...(isShopify
      ? [
          [
            t('exchanges.catalog.summary.online_store'),
            t(
              value.onlineStoreOnly
                ? 'exchanges.catalog.summary.hidden'
                : 'exchanges.catalog.summary.shown'
            )
          ] as [string, string]
        ]
      : [])
  ]
  const applies = [
    'exchanges.catalog.summary.applies_menu',
    isShopify
      ? 'exchanges.catalog.summary.applies_pages'
      : 'exchanges.catalog.summary.applies_pages_categories',
    'exchanges.catalog.summary.applies_search'
  ]
  return (
    <aside className="flex flex-col gap-4 font-roobert lg:w-[300px] lg:shrink-0">
      <Card variant="flat" className="flex flex-col gap-3 p-5">
        <p className="text-sm font-medium text-neutral-900">
          {t('exchanges.catalog.summary.title')}
        </p>
        <dl className="flex flex-col divide-y divide-solid divide-neutral-100 text-sm">
          {rows.map(([label, count]) => (
            <div key={label} className="flex justify-between gap-2 py-1.5">
              <dt className="text-neutral-600">{label}</dt>
              <dd className="font-medium text-neutral-900">{count}</dd>
            </div>
          ))}
        </dl>
        <p className="text-xs text-neutral-600">
          <Trans
            i18nKey={
              isShopify
                ? 'exchanges.catalog.summary.note'
                : 'exchanges.catalog.summary.note_categories'
            }
            components={{ b: <b /> }}
          />
        </p>
      </Card>
      <Card variant="flat" className="flex flex-col gap-3 p-5">
        <p className="text-sm font-medium text-neutral-900">
          {t('exchanges.catalog.summary.applies_title')}
        </p>
        <ul className="flex flex-col gap-1.5 text-sm text-neutral-900">
          {applies.map((key) => (
            <li key={key} className="flex items-center gap-2">
              <CheckIcon className="size-4 shrink-0 text-success-main" />
              {t(key)}
            </li>
          ))}
        </ul>
        <p className="text-xs text-neutral-600">
          {t('exchanges.catalog.summary.not_applies')}
        </p>
      </Card>
    </aside>
  )
}

const MenuDescription = () => (
  <Trans
    i18nKey="exchanges.catalog.menu_description"
    // Not <link>: Trans parses it as the empty HTML element and drops the text.
    components={{
      help: (
        <Button
          variant="ghost"
          size="sm"
          inline
          nativeButton={false}
          render={
            <a
              href="https://admin.shopify.com/menus"
              target="_blank"
              rel="noreferrer"
            />
          }
        />
      )
    }}
  />
)

interface MenuPickerProps {
  ecommerceId?: string
  value: string
  onChange: (menuId: string) => void
  readOnly: boolean
}

// A store has few menus, so they are all listed at once and filtered as typed.
function MenuPicker({
  ecommerceId,
  value,
  onChange,
  readOnly
}: MenuPickerProps) {
  const { t } = useTranslation()
  const {
    results: menus,
    searching,
    loading,
    loaded,
    failed
  } = useCatalogSearch(ecommerceId, SearchCatalogKindEnum.Menu, '', true)
  // Older installs of the REVER app can't read the store's menus, so the handle is typed instead.
  if (failed)
    return (
      <FieldRoot className="max-w-md">
        <Input
          aria-label={t('exchanges.catalog.menu_label')}
          placeholder="main-menu"
          value={value}
          onChange={(event) => onChange(event.currentTarget.value.trim())}
          disabled={readOnly}
        />
        <FieldDescription>
          {t('exchanges.catalog.menu_unlisted')}
        </FieldDescription>
      </FieldRoot>
    )
  const selected = menus.find((menu) => menu.id === value) ?? null
  // Only a lookup that worked can tell a deleted menu from one that didn't load.
  const missing = !!value && loaded && !searching && !selected
  return (
    <FieldRoot className="max-w-md">
      <ComboboxRoot<CatalogItem>
        items={menus}
        value={selected}
        onValueChange={(menu) => menu && onChange(menu.id)}
        itemToStringLabel={(menu) => menu.label}
        itemToStringValue={(menu) => menu.id}
        disabled={readOnly || loading}
      >
        <ComboboxInputGroup>
          <ComboboxInput
            aria-label={t('exchanges.catalog.menu_label')}
            placeholder={
              loading
                ? t('non_returnable.loading')
                : t('exchanges.catalog.menu_placeholder')
            }
          />
          <ComboboxChevronTrigger
            label={t('non_returnable.browse', {
              name: t('exchanges.catalog.menu_label')
            })}
            loading={loading}
            loadingLabel={t('non_returnable.loading')}
          />
        </ComboboxInputGroup>
        <ComboboxDropdown>
          {searching ? (
            <ComboboxLoading label={t('non_returnable.searching')} />
          ) : (
            <ComboboxEmpty>{t('non_returnable.no_results')}</ComboboxEmpty>
          )}
          <ComboboxList>
            {(menu: CatalogItem) => (
              <ComboboxCheckItem key={menu.id} value={menu}>
                <span>
                  {menu.label}
                  <ComboboxItemDetail>{menu.id}</ComboboxItemDetail>
                </span>
              </ComboboxCheckItem>
            )}
          </ComboboxList>
        </ComboboxDropdown>
      </ComboboxRoot>
      {missing ? (
        <FieldDescription>
          {t('exchanges.catalog.menu_missing', { menu: value })}
        </FieldDescription>
      ) : null}
      {value ? null : (
        <FieldDescription>{t('exchanges.catalog.menu_empty')}</FieldDescription>
      )}
    </FieldRoot>
  )
}

function ExclusionPicker({
  kind,
  copyKey,
  ecommerceId,
  value,
  saved,
  onChange,
  readOnly
}: {
  kind: CatalogKind
  copyKey: CatalogCopyKey
  ecommerceId?: string
  value: string[]
  saved: string[]
  onChange: (ids: string[]) => void
  readOnly: boolean
}) {
  const resolved = useCatalogItems(ecommerceId, kind, saved)
  const known = new Map(resolved.items.map((item) => [item.id, item]))
  // Only saved ids can be stale: anything picked here came from the store.
  const staleIds = resolved.resolved
    ? value.filter((id) => saved.includes(id) && !known.has(id))
    : []
  return (
    <CatalogPicker
      bare
      ecommerceId={ecommerceId}
      kind={kind}
      copyKey={copyKey}
      copyPrefix="exchanges.catalog"
      value={value}
      known={known}
      staleIds={staleIds}
      onChange={onChange}
      canEdit={!readOnly}
      loading={resolved.loading}
      preload
    />
  )
}
